import classNames from "classnames";
import { Inline, Stack } from "PFComponents/containers";
import { Tooltip, TooltipIcon } from "PFComponents/tooltip";
import { IconName } from "PFTheme/graphics/icons";
import { Id } from "PFTypes";
import React, { ReactNode } from "react";

import { Icon } from "../icon";
import { Typography } from "../typography";
import css from "./radio.module.scss";

export type RadioItem<ID_TYPE extends Id> = {
  id: ID_TYPE;
  /**
   * @deprecated Use label instead
   */
  customLabel?: ReactNode;
  iconName?: IconName;
  tooltipContent?: ReactNode;
  label?: string | ReactNode;
  sublabel?: string;
  disabled?: boolean;
  disabledMessage?: string;
};

type RadioProps<ID_TYPE extends Id> = {
  item: RadioItem<ID_TYPE>;
  onChange: (item: ID_TYPE) => void;
  checked: boolean;
  verticalItems?: boolean;
  name: string;
  disabled?: boolean;
  withMargin?: boolean;
};

export const Radio = <ID_TYPE extends Id>({
  checked,
  item,
  onChange,
  verticalItems,
  name,
  disabled,
  withMargin = true
}: RadioProps<ID_TYPE>) => {
  const showLabel = item.label || item.customLabel || item.iconName || item.tooltipContent;

  return (
    <label
      className={classNames(css.customRadio, {
        [css.disabled]: disabled,
        [css.vertical]: verticalItems,
        [css.margin]: withMargin
      })}
    >
      <input
        className={css.input}
        id={`${item.id}`}
        name={name}
        type="radio"
        checked={checked}
        onChange={() => onChange(item.id)}
        value={item.id}
        disabled={disabled}
      />
      <div className={css.customRadioWrapper}>
        <span className={css.customRadioButton} />
      </div>
      {showLabel && (
        <Tooltip content={item.disabledMessage} disabled={!(disabled && item.disabledMessage)}>
          <Stack gap="spacingSm">
            <Inline alignItems="center" className={css.labelContainer}>
              {item.customLabel && item.customLabel}
              {item.iconName && <Icon name={item.iconName} />}
              {item.label && (
                <Typography
                  withMargin
                  variant="bodyRegular"
                  tag="span"
                  className={classNames({
                    [css.disabled]: disabled
                  })}
                >
                  {item.label}
                </Typography>
              )}
              {item.tooltipContent && <TooltipIcon content={item.tooltipContent} />}
            </Inline>
            {item.sublabel && (
              <Typography variant="labelRegular" tag="span" className={css.sublabel}>
                {item.sublabel}
              </Typography>
            )}
          </Stack>
        </Tooltip>
      )}
    </label>
  );
};
